import { all, fork, put, select, takeLatest } from 'redux-saga/effects';
import {
  getProfilesAPI,
  updateProfileAPI,
  changePasswordAPI,
  REQUEST_PROFILE_FAILURE,
  ACTION_REQUEST_PROFILE,
  ACTION_CHANGE_PASSWORD,
  REQUEST_CHANGE_PASSWORD_SUCCESS,
  REQUEST_CHANGE_PASSWORD_FAILURE,
  ACTION_UPDATE_PROFILE,
  REQUEST_UPDATE_PROFILE_SUCCESS,
  REQUEST_UPDATE_PROFILE_FAILURE,
  ACTION_CONNECT,
  REQUEST_CONNECT_WALLET_SUCCESS,
  REQUEST_CONNECT_WALLET_FAILURE,
  connectWalletAPI,
  ACTION_FETCH_WALLETS,
  fetchWalletAPI,
  fetchLeaderboardAPI,
  actionFetchLeaderboard,
  fetchPointHistoryAPI,
  ACTION_DISCONNECT,
  REQUEST_DISCONNECT_WALLET_SUCCESS,
  REQUEST_DISCONNECT_WALLET_FAILURE,
  disconnectWalletAPI,
  actionGetProfile,
  ACTION_DELETE_ACCOUNT,
  REQUEST_DELETE_ACCOUNT_SUCCESS,
  REQUEST_DELETE_ACCOUNT_FAILURE,
  deleteAccountAPI,
  connectWalletXamanAPI,
  ACTION_CONNECT_XAMAN,
  REQUEST_CONNECT_XAMAN_WALLET_SUCCESS,
  REQUEST_CONNECT_XAMAN_WALLET_FAILURE,
  updateTrustLines,
  ACTION_GET_TRUST_LINE,
  getTrustLineAPI,
  createTrustLineAPI,
  ACTION_CREATE_TRUST_LINE,
  REQUEST_CREATE_TRUST_LINE_FAILURE,
  REQUEST_CREATE_TRUST_LINE_SUCCESS,
  ACTION_FETCH_LEADERBOARD,
} from './index';
import { showToastAlert } from '../app';
import { getErrorMessage } from '../../../utils/error';
import { fetchCountryAPI } from '../country';
import { selectedMonthAndYear } from '../../../utils/date';
import { RealtimeEventType } from '../../../constant/realtimeEvent';
import {
  REQUEST_UPLOAD_FILE_FAILURE,
  REQUEST_UPLOAD_FILE_SUCCESS,
  uploadFileAPI,
} from '../uploadFile';
import {
  ACTION_HANDLE_ADD_SHARE_LINK,
  actionResetBodyShareLink,
  actionResetShareLink,
  actionUpdateBodyShareLink,
  createShareLinkAPI,
  REQUEST_CREATE_SHARE_LINK_DATA_FAILURE,
  REQUEST_CREATE_SHARE_LINK_DATA_SUCCESS,
} from '../shareLink';
import { handleShareLink } from '../../../utils/shareSocials';

function* loadProfileData() {
  const { profile } = yield select();
  const { profileFilter, leaderboardFilter, pointHistoryFilter } = profile;
  yield all([
    put(actionResetBodyShareLink()),
    put(actionResetShareLink()),
    put(getProfilesAPI(profileFilter)),
    put(fetchCountryAPI()),
    put(fetchPointHistoryAPI(pointHistoryFilter)),
    put(actionFetchLeaderboard(leaderboardFilter)),
  ]);
}

function* handleGetProfile() {
  yield takeLatest([ACTION_REQUEST_PROFILE], function* (action) {
    yield put(getProfilesAPI(action.payload));
  });

  yield takeLatest([REQUEST_PROFILE_FAILURE], function* (action) {
    yield put(showToastAlert(action?.payload?.error?.message || 'Failed to get profile!', 'error'));
  });
}

function* handleChangePassword() {
  yield takeLatest([ACTION_CHANGE_PASSWORD], function* (action) {
    yield put(changePasswordAPI(action.payload));
  });
  yield takeLatest([REQUEST_CHANGE_PASSWORD_SUCCESS], function* () {
    yield put(showToastAlert('Change Password Succeed!', 'success'));
  });
  yield takeLatest([REQUEST_CHANGE_PASSWORD_FAILURE], function* (action) {
    const errorMessage = getErrorMessage(action.payload);
    yield put(showToastAlert(errorMessage || 'Password is not strong enough!', 'error'));
  });
}

function* handleUpdateProfile() {
  yield takeLatest([ACTION_UPDATE_PROFILE], function* (action) {
    yield put(updateProfileAPI(action.payload));
  });

  yield takeLatest([REQUEST_UPDATE_PROFILE_SUCCESS], function* () {
    yield put(showToastAlert('Update Profile Success', 'success'));
    yield put(actionGetProfile());
    const { profile } = yield select();
    const { leaderboardFilter } = profile;
    yield put(actionFetchLeaderboard(leaderboardFilter));
  });

  yield takeLatest([REQUEST_UPDATE_PROFILE_FAILURE], function* (action) {
    const errorMessage =
      action?.payload?.data?.message?.[0]?.error || action?.payload?.data?.message;
    yield put(showToastAlert(errorMessage || `Failed to Update Profile!`, 'error'));
  });
}

function* handleConnectWallet() {
  // Phantom and Metamask
  yield takeLatest([ACTION_CONNECT], function* (action) {
    yield put(connectWalletAPI(action.payload));
  });
  yield takeLatest([REQUEST_CONNECT_WALLET_SUCCESS], function* () {
    yield put(fetchWalletAPI());
    yield put(showToastAlert('Connect wallet successfully', 'success'));
  });
  yield takeLatest([REQUEST_CONNECT_WALLET_FAILURE], function* (action) {
    yield put(
      showToastAlert(getErrorMessage(action.payload) || `Failed to connect wallet`, 'error')
    );
  });

  // Xaman
  yield takeLatest([ACTION_CONNECT_XAMAN], function* (action) {
    yield put(connectWalletXamanAPI(action.payload));
  });
  yield takeLatest([REQUEST_CONNECT_XAMAN_WALLET_SUCCESS], function* (action) {
    if (action.payload?.url) return (window.location.href = action.payload?.url);
    yield put(
      showToastAlert(getErrorMessage(action.payload) || `Failed to connect wallet`, 'error')
    );
  });
  yield takeLatest([REQUEST_CONNECT_XAMAN_WALLET_FAILURE], function* (action) {
    yield put(
      showToastAlert(getErrorMessage(action.payload) || `Failed to connect wallet`, 'error')
    );
  });
}

function* handleListenTrustLine() {
  yield takeLatest([RealtimeEventType.TRUSTLINE_SUCCESS], function* (action) {
    const { profile } = yield select();
    const { trustLineWallets } = profile;
    const updatedTrustLineIdx = trustLineWallets.findIndex(
      (trustLine) => trustLine.cryptoCurrency === action?.payload?.token
    );
    if (updatedTrustLineIdx != -1) {
      trustLineWallets[updatedTrustLineIdx].hasTrustLine = true;
      yield put(updateTrustLines(trustLineWallets));
    }
  });

  yield takeLatest([RealtimeEventType.TRUSTLINE_FAILED], function* () {
    yield put(showToastAlert(`Failed to create trust line`, 'error'));
  });

  yield takeLatest([ACTION_GET_TRUST_LINE], function* () {
    yield put(getTrustLineAPI());
  });

  yield takeLatest([ACTION_CREATE_TRUST_LINE], function* (action) {
    yield put(createTrustLineAPI(action.payload));
  });

  yield takeLatest([REQUEST_CREATE_TRUST_LINE_FAILURE], function* (action) {
    yield put(
      showToastAlert(getErrorMessage(action.payload) || `Failed to create trust line`, 'error')
    );
  });

  yield takeLatest([REQUEST_CREATE_TRUST_LINE_SUCCESS], function* (action) {
    const deepLink = action?.payload?.data?.deepLink;
    if (deepLink) return (window.location.href = deepLink);
    yield put(showToastAlert(`Failed to create trust line`, 'error'));
  });
}

function* handleDisconnectWallet() {
  yield takeLatest([ACTION_DISCONNECT], function* (action) {
    yield put(disconnectWalletAPI(action.payload));
  });
  yield takeLatest([REQUEST_DISCONNECT_WALLET_SUCCESS], function* () {
    yield put(fetchWalletAPI());
    yield put(showToastAlert('Disconnect wallet successfully', 'success'));
  });
  yield takeLatest([REQUEST_DISCONNECT_WALLET_FAILURE], function* (action) {
    yield put(
      showToastAlert(getErrorMessage(action.payload) || `Failed to disconnect wallet`, 'error')
    );
  });
}

function* handleGetWallet() {
  yield takeLatest([ACTION_FETCH_WALLETS], function* () {
    yield put(fetchWalletAPI());
  });
}

function* handleFetchLeaderboard() {
  yield takeLatest([ACTION_FETCH_LEADERBOARD], function* (action) {
    const { month, year } = action.payload;
    const { startTime, endTime } = selectedMonthAndYear(month, year);
    yield put(
      fetchLeaderboardAPI({
        ...action.payload,
        startTime,
        endTime,
      })
    );
  });
}

function* handleDeleteAccount() {
  yield takeLatest([ACTION_DELETE_ACCOUNT], function* (action) {
    yield put(deleteAccountAPI(action.payload));
  });

  yield takeLatest([REQUEST_DELETE_ACCOUNT_SUCCESS], function* () {
    yield put(showToastAlert('Delete Account Success', 'success'));
    window.location.href = '/login';
  });

  yield takeLatest([REQUEST_DELETE_ACCOUNT_FAILURE], function* (action) {
    const errorMessage =
      action?.payload?.data?.message?.[0]?.error || action?.payload?.data?.message;
    yield put(showToastAlert(errorMessage || `Failed to Delete Account!`, 'error'));
  });
}

function* handleAddShareLink() {
  yield takeLatest([ACTION_HANDLE_ADD_SHARE_LINK], function* (action) {
    const { payload } = action;
    const { file } = payload;
    yield put(uploadFileAPI(file));
  });

  yield takeLatest([REQUEST_UPLOAD_FILE_SUCCESS], function* (action) {
    const { shareLinkStore } = yield select();
    const { shareLinkBody } = shareLinkStore;
    const image = action?.payload?.data?.url;

    yield put(
      actionUpdateBodyShareLink({
        ...shareLinkBody,
        image,
      })
    );
    yield put(
      createShareLinkAPI({
        ...shareLinkBody,
        image,
      })
    );
  });

  yield takeLatest([REQUEST_CREATE_SHARE_LINK_DATA_SUCCESS], function* (action) {
    const newShareLink = action.payload.data;
    const type = action.payload?.type;
    const { shareLinkStore } = yield select();
    const { shareLinkBody } = shareLinkStore;
    yield handleShareLink(type || shareLinkBody.type, newShareLink);
  });

  yield takeLatest(
    [REQUEST_UPLOAD_FILE_FAILURE, REQUEST_CREATE_SHARE_LINK_DATA_FAILURE],
    function* (action) {
      const errorMessage = getErrorMessage(action.payload) || `Failed to share!`;
      yield put(showToastAlert(errorMessage, 'error'));
    }
  );
}

export function* loadProfilePage() {
  yield all([
    fork(loadProfileData),
    fork(handleGetProfile),
    fork(handleChangePassword),
    fork(handleUpdateProfile),
    fork(handleConnectWallet),
    fork(handleGetWallet),
    fork(handleFetchLeaderboard),
    fork(handleDisconnectWallet),
    fork(handleDeleteAccount),
    fork(handleListenTrustLine),
    fork(handleAddShareLink),
  ]);
}
